/**
 * DeliverySLAReportPage — `/reports/delivery-sla`.
 *
 * On-time vs late delivery breakdown. Wired to
 * GET /v1/reports/delivery-sla?from=&to=&serviceClass= on
 * service-reporting-api PreBuiltReportsController.
 */
import { type JSX, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Truck } from 'lucide-react';

import { getDeliverySLAReport } from '../../lib/api/pre-built-reports';
import { ApiError } from '../../lib/api/client';
import { findPreBuiltReport } from '../../lib/api/reports';
import { DeferredNotice } from '../shared/StaffUI';
import { KPICard } from '../mail/shared/MailUI';
import { ReportShell } from './ReportShell';

function todayIso(): string { return new Date().toISOString().slice(0, 10); }
function thirtyDaysAgoIso(): string {
  const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().slice(0, 10);
}

export function DeliverySLAReportPage(): JSX.Element {
  const report = findPreBuiltReport('delivery-sla');
  const [from, setFrom] = useState<string>(thirtyDaysAgoIso());
  const [to, setTo] = useState<string>(todayIso());
  const [serviceClass, setServiceClass] = useState<string>('');

  const query = useQuery({
    queryKey: ['pre-built', 'delivery-sla', from, to, serviceClass],
    queryFn: () => getDeliverySLAReport({ from, to, serviceClass: serviceClass || undefined }),
    retry: false,
  });

  if (!report) return <p>Unknown report.</p>;

  const data = query.data;
  const denied =
    query.isError && query.error instanceof ApiError &&
    (query.error.status === 401 || query.error.status === 403);

  const latePct = data ? (data.totalItems > 0 ? Math.round(((data.totalItems - (data.rows.reduce((a, r) => a + r.onTime, 0))) / data.totalItems) * 100) : 0) : null;
  const failedCount = data ? data.rows.reduce((a, r) => a + r.failed, 0) : null;

  return (
    <ReportShell report={report}>
      {/* Filters */}
      <div className="bg-white border border-border rounded-xl p-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div>
            <label className="text-[10px] font-semibold text-muted uppercase tracking-widest mb-1 block">From</label>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} aria-label="From date"
              className="w-full px-3 py-2 text-sm border border-border rounded-lg" />
          </div>
          <div>
            <label className="text-[10px] font-semibold text-muted uppercase tracking-widest mb-1 block">To</label>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} aria-label="To date"
              className="w-full px-3 py-2 text-sm border border-border rounded-lg" />
          </div>
          <div>
            <label className="text-[10px] font-semibold text-muted uppercase tracking-widest mb-1 block">Service class</label>
            <select value={serviceClass} onChange={(e) => setServiceClass(e.target.value)} aria-label="Service class"
              className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-white">
              <option value="">All classes</option>
              <option value="STANDARD">STANDARD</option>
              <option value="EXPRESS">EXPRESS</option>
              <option value="EMS">EMS</option>
              <option value="REGISTERED_MAIL">REGISTERED_MAIL</option>
            </select>
          </div>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard title="On-time %" value={data ? `${data.onTimePct}%` : '—'} hint="Reporting" icon={Truck} tone={data && data.onTimePct >= 85 ? 'success' : 'warning'} />
        <KPICard title="Late %" value={latePct !== null ? `${latePct}%` : '—'} hint="late + in-transit" />
        <KPICard title="Failed" value={failedCount !== null ? failedCount.toLocaleString('en-US') : '—'} hint="returned / failed" tone={failedCount !== null && failedCount > 0 ? 'danger' : undefined} />
        <KPICard title="Total items" value={data ? data.totalItems.toLocaleString('en-US') : '—'} hint="in period" />
      </div>

      {query.isPending && <p className="text-sm text-muted">Loading report…</p>}
      {denied && (
        <DeferredNotice title="Permission required">
          <code className="font-mono">Reports/readDeliverySLA</code> required.
        </DeferredNotice>
      )}
      {query.isError && !denied && (
        <DeferredNotice title="Report unavailable">{query.error.message}</DeferredNotice>
      )}

      {data && (
        <div className="bg-white border border-border rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-900">Breakdown by service class</h3>
            {data.stub && (
              <span className="text-[10px] font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
                Preview data — full data coming soon
              </span>
            )}
          </div>
          {data.rows.length === 0 ? (
            <p className="text-sm text-muted py-4 text-center">No delivery data found for the period.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
                  <tr>
                    <th className="text-left px-4 py-2">Service class</th>
                    <th className="text-right px-4 py-2">Total</th>
                    <th className="text-right px-4 py-2">On-time</th>
                    <th className="text-right px-4 py-2">Late</th>
                    <th className="text-right px-4 py-2">Failed</th>
                    <th className="text-right px-4 py-2">On-time %</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {data.rows.map((r) => (
                    <tr key={r.serviceClass}>
                      <td className="px-4 py-2 text-xs font-mono">{r.serviceClass}</td>
                      <td className="px-4 py-2 text-right text-xs">{r.total.toLocaleString('en-US')}</td>
                      <td className="px-4 py-2 text-right text-xs text-nipost-green">{r.onTime.toLocaleString('en-US')}</td>
                      <td className="px-4 py-2 text-right text-xs text-amber-700">{r.late.toLocaleString('en-US')}</td>
                      <td className="px-4 py-2 text-right text-xs text-red-600">{r.failed.toLocaleString('en-US')}</td>
                      <td className="px-4 py-2 text-right text-xs font-semibold">{r.onTimePct}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </ReportShell>
  );
}
